import React, { useEffect, useMemo, useRef, useState } from "react";
import { getTenantKey, setRefreshToken, setToken } from "./utils";
import { queryClient } from "./queryClient";
import { CognitoUserPool } from "amazon-cognito-identity-js";
import { useNavigate } from "react-router-dom";
import posthog from "posthog-js";
import { Notification } from "../pages/notification/Notification";
import { api } from "./api";
import { setAppContextRef } from "./app-context-bridge";
import { clearAllNotificationQueueDedupe } from "../pages/badge-center/notification-popups/useNotificationQueue";
import { resetPushRegistration } from "./request";
import { clearDatadogUser } from "./datadog";
import { ShowToast } from "./toast";
import { getLoggerRef } from "../integrations/logger/logger-bridge";
import { useSessionRefreshTimer } from "./useSessionRefreshTimer";
import { redirectToLogin } from "./axios";
import { PUBLIC_ROUTES } from "../constant";
import { authApi, isBackendSession, toUserSession } from "./authApi";
import { isBackendAuthEnabled } from "../integrations/posthog/featureFlags";

export type AppContextType = {
  tenantLoading: boolean;
  tenantProfile: any;
  tenantError: any;
  cognitoUserRef: any;
  userPool: any;
  userSession: any;
  userSessionLoading: boolean;
  refreshUserSession: () => void;
  logout: () => Promise<void>;
  isLoggingOut: boolean;
  fetchUserSession: () => void;
  setUserSession: (session: any) => void;
  styles: any;
  setNotificationModal: (modal: boolean) => void;
  notificationModal: boolean;
  setProfile: any;
  preLoginLogos: any;
  setIsChatbotOpen: any;
  isChatbotOpen: boolean;
  fetchPreLoginLogos: () => void;
  certificationAssets: any;
  setCertificationAssets: (assets: any) => void;
  fetchCertificationAssets: () => void;
  isCertificationAssetsLoading: boolean;
  setIsCertificationAssetsLoading: (loading: boolean) => void;
}

export const AppContext = React.createContext<AppContextType>({
  tenantLoading: false,
  tenantProfile: null,
  tenantError: null,
  cognitoUserRef: null,
  userPool: null,
  userSession: null,
  userSessionLoading: false,
  refreshUserSession: () => {},
  logout: async () => {},
  isLoggingOut: false,
  fetchUserSession: () => {},
  setUserSession: () => {},
  styles: null,
  setNotificationModal: () => {},
  notificationModal: false,
  setProfile: () => {},
  preLoginLogos: null,
  setIsChatbotOpen: () => {},
  isChatbotOpen: false,
  fetchPreLoginLogos: () => {},
  certificationAssets: null,
  setCertificationAssets: () => {},
  fetchCertificationAssets: () => {},
  isCertificationAssetsLoading: false,
  setIsCertificationAssetsLoading: () => {},
});
/**
 * Apply styling from the json file to the root element
 * @param styles
 */
const applyStylingFromJson = (styles) => {
  if (styles.colors) {
    const root = document.documentElement;
    Object.keys(styles.colors).forEach((key) => {
      if (typeof styles.colors[key] === "string") {
        root.style.setProperty(`--${key}`, styles.colors[key]);
      } else if (typeof styles.colors[key] === "object") {
        Object.keys(styles.colors[key]).forEach((subKey) => {
          root.style.setProperty(`--${key}-${subKey}`, styles.colors[key][subKey]);
        });
      }
    });
  }
}

export function AppProvider({ children }) {
  const navigate = useNavigate();
  const key = `${getTenantKey()}/configuration/profile.json`;
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [profile, setProfile] = useState<any>();
  const [certificationAssets, setCertificationAssets] = useState<any>();
  const [preLoginLogos, setPreLoginLogos] = useState<any>();
  const stylesKey = `${getTenantKey()}/configuration/styles.json`;
  const cognitoUserRef = useRef<any>();
  const userPoolRef = useRef<any>();
  const isFetchingUserSession = useRef<boolean>(false);
  const [userSession, setUserSession] = useState<any>();
  const [userSessionLoading, setUserSessionLoading] = useState<boolean>(true);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  const [isFeatureFlagLoading, setIsFeatureFlagLoading] =
    useState<boolean>(true);
  const [styles, setStyles] = useState<any>();
  const [notificationModal, setNotificationModal] = useState<boolean>(false);
  const [isChatbotOpen, setIsChatbotOpen] = useState<boolean>(false);
  const [isCertificationAssetsLoading, setIsCertificationAssetsLoading] = useState<boolean>(true);

  const fetchPreLoginLogos = async () => {
    const preLoginLogos = await api.getPreLoginLogos();
    setPreLoginLogos(preLoginLogos?.data?.logos);
  }


  const fetchPreLogin = async (loading = true) => {
    // Local, not state: fetchUserSession's own effect fires off of the `profile`
    // state update, so this closure's `profile` stays stale for the rest of this
    // call. Tracks whether *this* call reached setProfile before failing.
    let profileWasSet = false;
    try {
      if (loading) {
        setLoading(true);
      }
      const preLogin = await api.getPreLogin();
      const style = preLogin.data?.styles;
      applyStylingFromJson(style);
      setStyles(style);
      if (loading) {
        const object = {
          user_pool_client_id: preLogin.data?.profile?.userPoolClientId,
          user_pool_id: preLogin.data?.profile?.userPoolId,
          user_pool_name: preLogin.data?.profile?.userPoolName,
          sso_redirect_url: preLogin.data?.profile?.ssoRedirectUrl,
          sso_client_id: preLogin.data?.profile?.ssoClientId,
          user_pool_cognito_domain: preLogin.data?.profile?.userPoolCognitoDomain,
          routeNames: preLogin.data?.profile?.routeNames ?? {},
        }
        setProfile(object);
        profileWasSet = true;
      }
      await fetchPreLoginLogos();
      if (loading) {
        setLoading(false);
      }
    } catch (err) {
      // A background refresh only re-reads styles and logos for a session that
      // is already running, so a failure costs the user nothing. Promoting it
      // to tenant error state would tear a signed-in user down over a blip.
      if (!loading) {
        return;
      }
      setError("Failed to fetch pre-login data");
      // Only clear userSessionLoading if this call never reached setProfile: in
      // that case fetchUserSession's `if (profile)` guard is permanently false, so
      // nothing else will ever clear it, stranding Main's login gate. But if
      // setProfile already ran (e.g. the failure was in fetchPreLoginLogos, which
      // runs after it), fetchUserSession's effect has already fired and its own
      // (independently in-flight) Cognito check will resolve the flag itself —
      // clearing it here too would open a window where Main's redirect-to-/login
      // gate can fire before that check completes, signing out a valid session.
      if (!profileWasSet) {
        setUserSessionLoading(false);
      }
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchPreLogin();
  }, []);

  /**
   * Only way the session is refreshed. Publishing the new session is what
   * schedules the next background refresh, via useSessionRefreshTimer below, so
   * refreshing anywhere else would fork a second chain of timers.
   *
   * `callback` receives the new session, or undefined when the refresh failed.
   */
  const refreshUserSession = (callback?: (session?: any) => void) => {
    if (isBackendSession()) {
      authApi
        .refresh()
        .then(({ idToken }) => {
          if (!idToken) {
            throw new Error("Backend refresh returned no ID token");
          }
          const session = toUserSession(idToken);
          setToken(idToken);
          setUserSession(session);
          callback?.(session);
        })
        .catch((err) => {
          getLoggerRef()?.warn("Session refresh failed", {
            errorName: (err as Error)?.name,
            errorMessage: (err as Error)?.message,
          });
          callback?.(undefined);
        });
      return;
    }
    if (cognitoUserRef.current) {
      cognitoUserRef.current.refreshSession(
        userSession?.refreshToken,
        (err, session) => {
          // Nothing to do on failure beyond recording it: the next API call gets
          // a 401 that the axios interceptor turns into a refresh-or-redirect.
          // Signing the user out from here would end a session the interceptor
          // could still recover. An expired refresh token is routine, hence warn
          // rather than error.
          if (err) {
            getLoggerRef()?.warn("Session refresh failed", {
              errorName: (err as Error)?.name,
              errorMessage: (err as Error)?.message,
            });
          }
          // A failed refresh returns no session; overwriting with it would wipe a
          // session that is still usable when the failure was only a network blip.
          if (session) {
            setToken(session?.idToken?.jwtToken);
            setRefreshToken(session?.refreshToken?.token);
            setUserSession(session);
          }
          if (callback) {
            callback(session);
          }
        }
      );
    }
  };

  const refreshBackgroundUserSession = () => {
    refreshUserSession(async (session) => {
      if (session) {
        await fetchPreLogin(false);
      }
    });
  }


  const fetchCertificationAssets = async () => {
    try {
      setIsCertificationAssetsLoading(true);
      const certificationAssetsResponse = await api.getCertificationAssets();
      setCertificationAssets(certificationAssetsResponse.data);
    } finally {
      setIsCertificationAssetsLoading(false);
    }
  }

  const removeToken = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("id_token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("auth_method");
    clearAllNotificationQueueDedupe();
  }


  useSessionRefreshTimer(userSession, refreshBackgroundUserSession);

  /** Drops everything this browser holds about the signed-in user. */
  const clearLocalSession = () => {
    clearDatadogUser();
    setUserSession(null);
    removeToken();
    queryClient.clear();
    cleanLocalStorage();
  }

  const logout = async () => {
    // Backend logout stays under the flag: flag OFF means no backend sessions
    // can exist (no mid-session flip), so a backend session with the flag off
    // is unreachable and falls through to the legacy path below.
    if (isBackendAuthEnabled() && isBackendSession()) {
      setIsLoggingOut(true);
      try {
        await authApi.logout();
        clearLocalSession();
      } catch (error) {
        // Status and code only — never response data or headers, which can
        // carry the cookie.
        getLoggerRef()?.error("Logout failed", error as Error, {
          status: (error as { response?: { status?: number } })?.response?.status,
          code: (error as { code?: string })?.code,
        });
        ShowToast({
          message:
            "Logout failed and you're still signed in. Please try again, or contact support if it continues.",
          type: "error",
        });
        throw error;
      } finally {
        setIsLoggingOut(false);
      }
      return;
    }

    if (cognitoUserRef.current) {
      cognitoUserRef.current.globalSignOut({
        onSuccess: () => {
          cognitoUserRef.current?.signOut();
          clearLocalSession();
        },
        onFailure: (err) => {
          // TODO: integrate with posthog
          cognitoUserRef.current?.signOut();
          clearLocalSession();
        },
      });
    } else {
      clearLocalSession();
    }
  };

  // clean local storage
  const cleanLocalStorage = () => {
    localStorage.removeItem("user");
    // Clear the per-session onboarding guard so a subsequent login in the same tab
    // re-evaluates whether the questionnaire modal should be shown.
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith("praxisOnboardingShown:"))
      .forEach((key) => sessionStorage.removeItem(key));
    // Logout never reloads the page, so the cached push registration would
    // otherwise keep this device bound to the account that just signed out.
    resetPushRegistration();
  }

  /**
   * Restores the session on load from the HttpOnly refresh cookie, which is the
   * only thing that survives a reload on the backend path — there is no Cognito
   * SDK state in localStorage to read back.
   */
  const fetchBackendUserSession = async () => {
    try {
      const { idToken } = await authApi.refresh();
      if (!idToken) {
        throw new Error("Backend refresh returned no ID token");
      }
      setToken(idToken);
      setUserSession(toUserSession(idToken));
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        // No usable cookie just means nobody is signed in on this browser, which
        // is the normal state on a public route.
        if (!PUBLIC_ROUTES.includes(window.location.pathname)) {
          redirectToLogin();
        }
      } else {
        // Anything else — a statusless network error, a 5xx — is not proof the
        // cookie is dead. redirectToLogin wipes auth_method and the stored
        // tokens, so the next load would skip this restore and a blip would
        // become a permanent sign-out.
        getLoggerRef()?.warn("Session restore failed", {
          errorName: (err as Error)?.name,
          errorMessage: (err as Error)?.message,
        });
      }
    } finally {
      setUserSessionLoading(false);
      isFetchingUserSession.current = false;
    }
  };

  const fetchUserSession = () => {
    if (profile) {
      if (isFetchingUserSession.current) {
        return;
      }
      isFetchingUserSession.current = true;
      setUserSessionLoading(true);
      if (isBackendSession()) {
        void fetchBackendUserSession();
        return;
      }
      const poolData = {
        UserPoolId: (profile as any)?.user_pool_id,
        ClientId: (profile as any)?.user_pool_client_id,
      };
      userPoolRef.current = new CognitoUserPool(poolData);
      var cognitoUser = userPoolRef.current.getCurrentUser();
      if (cognitoUser != null) {
        cognitoUser.getSession(async function (err, session) {
          isFetchingUserSession.current = false;
          if (err) {
            setUserSessionLoading(false);
            if (!PUBLIC_ROUTES.includes(window.location.pathname)) {
              redirectToLogin();
            }
            return;
          }
          if (session) {
            setTimeout(() => {
              setUserSessionLoading(false);
            }, 100);
            cognitoUserRef.current = cognitoUser;
            setUserSession(session);
            navigate(`${window.location.pathname}${window.location.search}`);
          }
        });
      } else {
        setUserSessionLoading(false);
        isFetchingUserSession.current = false;
      }
    }
  };

  useEffect(() => {
    fetchUserSession();
  }, [profile?.user_pool_client_id]);

  // track global error
  useEffect(() => {
    window.addEventListener("error", (event) => {
      fetchUserSession();
    });
  }, []);

  useEffect(() => {
    try {
      posthog.onFeatureFlags(function (flags, variants) {
        setIsFeatureFlagLoading(false);
      });
    } catch (error) {
      setIsFeatureFlagLoading(false);
    }
  }, []);

  const values = useMemo(() => ({
    tenantLoading: loading,
    tenantProfile: profile,
    tenantError: error,
    cognitoUserRef,
    userPool: userPoolRef.current,
    userSession,
    userSessionLoading,
    logout,
    isLoggingOut,
    refreshUserSession,
    fetchUserSession,
    setUserSession,
    styles,
    setNotificationModal,
    notificationModal,
    setProfile,
    preLoginLogos,
    isChatbotOpen,
    setIsChatbotOpen,
    fetchPreLoginLogos,
    certificationAssets,
    setCertificationAssets,
    fetchCertificationAssets,
    isCertificationAssetsLoading,
    setIsCertificationAssetsLoading,
  }), [loading, profile, error, cognitoUserRef, userPoolRef, userSession, userSessionLoading, logout, isLoggingOut, refreshUserSession, fetchUserSession, setUserSession, styles, setNotificationModal, notificationModal, setProfile, preLoginLogos, isChatbotOpen, setIsChatbotOpen, fetchPreLoginLogos, certificationAssets, setCertificationAssets, fetchCertificationAssets, isCertificationAssetsLoading, setIsCertificationAssetsLoading]);

  React.useEffect(() => {
    setAppContextRef(values);
  }, [values]);

  if (isFeatureFlagLoading) {
    return <div />;
  }

  if (loading) {
    return <div id="tenant-loading" />;
  }

  return (
    <AppContext.Provider value={values}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp(): {
  tenantLoading: boolean;
  tenantProfile: any;
  tenantError: any;
  cognitoUserRef: any;
  userPool: any;
  userSession: any;
  userSessionLoading: boolean;
  refreshUserSession: () => void;
  logout: () => Promise<void>;
  isLoggingOut: boolean;
  fetchUserSession: () => void;
  setUserSession: (session: any) => void;
  styles: any;
  setNotificationModal: (modal: boolean) => void;
  notificationModal: boolean;
  setProfile: any;
  preLoginLogos: any;
  setIsChatbotOpen: any;
  isChatbotOpen: boolean;
  fetchPreLoginLogos: () => void;
  certificationAssets: any;
  setCertificationAssets: (assets: any) => void;
  fetchCertificationAssets: () => void;
  isCertificationAssetsLoading: boolean;
  setIsCertificationAssetsLoading: (loading: boolean) => void;
} {
  const context = React.useContext(AppContext);
  if (context === undefined) {
    throw new Error("useApp must be used within a AppProvider");
  }
  return context as AppContextType;
}
